import { LitElement, css, html, nothing } from '@lit'

class NovaSearchResult extends LitElement {
  static properties = {
    noResults: { type: String },
    searchAgain: { type: String },
    searchData: { type: Object },
    searchTerm: { type: String },
    noSearchResult: { type: Boolean },
    suggestedResult: { type: Object },
    openState: { type: String },
  }

  connectedCallback() {
    super.connectedCallback()
    this.searchData = {}
    this.searchTerm = ''
    this.noSearchResult = false
    this.suggestedResult = null

    window.addEventListener('resize', this.handleRenderHeight.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('resize', this.handleRenderHeight.bind(this))
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (this.openState === 'closed') return
    if (Reflect.ownKeys(this.searchData).length === 0) return
    const { products, collections, articles, pages } =
      this.searchData.resources.results
    this.noSearchResult = [products, collections, articles, pages].every(
      (arr) => arr.length === 0,
    )

    if (this.suggestedResult === null && this.noSearchResult) {
      this.handleResult()
    } else if (this.noSearchResult) {
      this.handleRenderResult(this.suggestedResult)
    }

    this.handleRenderHeight()
  }

  async handleResult() {
    try {
      const response = await fetch(
        `?section_id=${window.main_suggested_result_section_id}`,
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        console.error(`HTTP error! Reason: ${response}`)
      }
      const data = await response.text()
      const parser = new DOMParser()
      const htmlDocument = parser.parseFromString(data, 'text/html')
      this.suggestedResult = htmlDocument
      this.handleRenderResult(this.suggestedResult)
    } catch (error) {
      console.error('Error:', error.message)
    }
  }

  handleRenderHeight() {
    if (this.openState === 'closed') return

    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      const resultRec = this.getBoundingClientRect()
      if (window.innerHeight - resultRec.bottom > 0) {
        this.style.maxHeight = 'unset'
      } else {
        this.style.maxHeight = `${window.innerHeight - resultRec.top}px`
      }
    }, 500)
  }

  handleRenderResult(data) {
    const suggestedResult = data.querySelector('.suggested-results')
    if (!suggestedResult) return

    const listContainer = suggestedResult.querySelector(
      '.header-search__list-container',
    )
    const currentListContainer = this.shadowRoot.querySelector(
      '.header-search__list-container',
    )
    if (listContainer && currentListContainer) {
      currentListContainer.replaceWith(listContainer.cloneNode(true))
    }

    const productContainer = suggestedResult.querySelector(
      '.header-search__product-container',
    )
    const currentProductContainer = this.shadowRoot.querySelector(
      '.header-search__product-container',
    )
    if (productContainer && currentProductContainer) {
      currentProductContainer.replaceWith(productContainer.cloneNode(true))
    }
  }

  render() {
    if (Reflect.ownKeys(this.searchData).length === 0) return nothing
    if (this.noSearchResult) {
      return html`
        <div class="header-search__result">
          <div class="header-search__no-result-container">
            <div class="header-search__no-result-content">
              <h3 class="header-search__no-result-title">
                ${this.noResults} "${this.searchTerm}"
              </h3>
              <p class="header-search__no-result-description">
                ${this.searchAgain}
              </p>
            </div>
            <div class="header-search__list-container"></div>
          </div>
          <div class="header-search__product-container"></div>
        </div>
      `
    }
    const displayProducts = this.searchData.resources.results.products.slice(
      0,
      4,
    )

    return html`
        <div class="header-search__result">
          ${
            this.searchData.resources.results.products.length > 0
              ? html` <div class="header-search__product-container">
                <h3>Products</h3>
                <div class="header-search__product-slide-container">
                  <div class="header-search__product-slide-inner-container">
                    ${displayProducts.map(
                      (product) => html`
                        <div class="header-search__product-card">
                          <a href=${product.url}>
                            <img
                              src=${product.image}
                              alt=${product.title}
                            />
                            <h4>${product.title}</h4>
                            <p>
                              $${product.price}
                              ${
                                product.compare_at_price_max !== '0.00'
                                  ? html`
                                  <span
                                    class="header-search__product-compared-price"
                                    >$${product.compare_at_price_max}</span>
                                  `
                                  : null
                              }
                            </p>
                          </a>
                        </div>
                      `,
                    )}
                  </div>
                </div>
              </div>`
              : nothing
          }
          <div class="header-search__list-container">
            ${
              this.searchData.resources.results.collections.length > 0
                ? html` <div class="header-search__collection-list">
                  <h3>Collections</h3>
                  <ul>
                    ${this.searchData.resources.results.collections.map(
                      (collection) => html`
                        <li>
                          <a href=${collection.url}>${collection.title}</a>
                        </li>
                      `,
                    )}
                  </ul>
                </div>`
                : nothing
            }
            ${
              this.searchData.resources.results.articles.length > 0
                ? html` <div class="header-search__article-list">
                  <h3>Blog</h3>
                  <ul>
                    ${this.searchData.resources.results.articles.map(
                      (article) => html`
                        <li><a href=${article.url}>${article.title}</a></li>
                      `,
                    )}
                  </ul>
                </div>`
                : nothing
            }
            ${
              this.searchData.resources.results.pages.length > 0
                ? html` <div class="header-search__page-list">
                  <h3>Pages</h3>
                  <ul>
                    ${this.searchData.resources.results.pages.map(
                      (page) => html`
                        <li><a href=${page.url}>${page.title}</a></li>
                      `,
                    )}
                  </ul>
                </div>`
                : nothing
            }
          </div>
        </div>
      `
  }

  static styles = css`
    :host .header-search__result {
      display: flex;
      width: 100%;
      overflow: hidden;
      flex-direction: column;
      max-height: inherit;
      overflow-y: auto;
      -ms-overflow-style: none; 
      scrollbar-width: none; 
    }

    :host .header-search__result::-webkit-scrollbar {
      display: none;
      width: 0; 
    }

    :host .header-search__product-container {
      width: 100%;
    }

    :host .header-search__list-container {
      display: flex;
      flex-direction: column;
      padding: var(--nova-space-md) 0;
      width: 100%;
      gap: var(--nova-space-md);
    }

    @media screen and (min-width: 990px) {
      :host .header-search__result {
        flex-direction: row;
        justify-content: space-between;
        padding-bottom: var(--nova-space-sm);
      }

      :host .header-search__list-container {
        order: 1;
        width: fit-content;
        flex-shrink: 0;
        padding: var(--nova-space-none);
      }

      :host .header-search__product-container {
        order: 2;
        width: fit-content;
      }
    }

    :host .header-search__product-slide-container {
      overflow-x: auto;
      width: 100%;
      max-width: 100%;
      -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
      scrollbar-width: none; /* Hide scrollbar for Firefox */
    }

    /* Hide scrollbar for Chrome, Safari and Opera */
    :host .header-search__product-slide-container::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__product-slide-inner-container {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      width: fit-content;
      gap: var(--nova-space-xs);
    }

    :host h3 {
      font: var(--nova-h5-font);
      margin: 0 0 var(--nova-space-sm);
    }

    @media screen and (min-width: 768px) {
      :host h3 {
        font: var(--nova-h6-font);
      }
    }

    :host a {
      color: inherit;
      text-decoration: none;
    }

    :host ul {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-xs);
      padding-left: var(--nova-space-none);
      margin: var(--nova-space-none);
    }

    :host li {
      list-style: none;
      color: var(--nova-color-subdued);
    }

    :host .header-search__no-result-title {
      font: var(--nova-h5-font);
      margin: 0 0 var(--nova-space-2xs);
    }

    :host .header-search__no-result-description {
      color: var(--nova-color-subdued);
      margin: var(--nova-space-none);
    }

    :host .header-search__no-result-container {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-md);
    }

    /* the following styles are for the temperary product cards, can be removed once the custom product card component is ready */
    :host .header-search__product-card {
      width: 160px;
      padding-bottom: var(--nova-space-md);
    }

    :host .header-search__product-card img {
      width: 100%;
      aspect-ratio: 160 / 214;
      object-fit: cover;
      object-position: center;
    }

    :host h4 {
      font: var(--nova-h6-font);
      margin: var(--nova-space-none);
    }

    :host p {
      margin: var(--nova-space-none);
    }

    :host .header-search__product-compared-price {
      color: var(--nova-color-subdued);
      text-decoration: line-through;
    }

    @media screen and (min-width: 1300px) {
      :host .header-search__product-card {
        width: 220px;
      }

      :host h4 {
        font-size: 18px;
      }
    }
  `
}

customElements.define('nova-search-result', NovaSearchResult)
